import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, CheckCircle, XCircle, AlertCircle, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SEOHead from '../components/SEOHead';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const RegulationDetail = () => {
  const { countryCode } = useParams();
  const [regulation, setRegulation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegulation();
  }, [countryCode]);

  const fetchRegulation = async () => {
    try {
      const response = await axios.get(`${API}/regulations/${countryCode}`);
      setRegulation(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-gray-700 text-lg">Loading...</div>
      </div>
    );
  }

  const country = regulation;

  if (!regulation) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-md">
            <CardContent className="pt-6">
              <p className="text-gray-700">Country regulations not found.</p>
              <Link to="/regulations">
                <Button className="mt-4 bg-red-700 hover:bg-red-900 text-white">
                  Back to Regulations
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getConsentBadge = (type) => {
    return type === 'Opt-Out' ? 'bg-green-700 text-white' : 'bg-blue-700 text-white';
  };

  // Schema.org structured data for Country Regulation Detail
  const regulationSchema = regulation ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${regulation.name} Donation Regulations`,
    "description": `Complete guide to organ donation laws, transplant procedures, medical visa requirements, and euthanasia regulations for ${regulation.name}.`,
    "url": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/regulations/${countryCode}`,
    "author": {
      "@type": "NGO",
      "name": "DonorsHouse",
      "parentOrganization": {
        "@type": "NGO",
        "name": "New Abilities Foundation"
      }
    },
    "publisher": {
      "@type": "NGO",
      "name": "DonorsHouse",
      "parentOrganization": {
        "@type": "NGO",
        "name": "New Abilities Foundation"
      },
      "logo": {
        "@type": "ImageObject",
        "url": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/favicon.png`
      }
    },
    "about": {
      "@type": "Thing",
      "name": "Organ Donation Regulations",
      "description": `Regulations for ${regulation.name}`
    },
    "mainEntity": {
      "@type": "Place",
      "name": regulation.name,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": regulation.code
      }
    }
  } : null;

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  const breadcrumbs = regulation ? [
    { name: 'Home', url: siteUrl },
    { name: 'Regulations', url: `${siteUrl}/regulations` },
    { name: regulation.name, url: `${siteUrl}/regulations/${countryCode}` }
  ] : [];

  return (
    <>
      {regulation && (
        <SEOHead
          title={`${regulation.name} Donation Regulations | Complete Guide | DonorsHouse`}
          description={`Complete guide to organ donation laws, transplant procedures, medical visa requirements, and euthanasia regulations for ${regulation.name}. Find out if foreigners can donate or receive transplants.`}
          keywords={`${regulation.name} donation regulations, ${regulation.name} organ donation, ${regulation.name} transplant laws, ${regulation.name} medical visa, ${regulation.name} euthanasia laws, foreign national donation ${regulation.name}`}
          schema={regulationSchema}
          type="article"
          author="DonorsHouse"
          publishedTime={regulation.created_at || new Date().toISOString()}
          breadcrumbs={breadcrumbs}
        />
      )}
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/regulations">
          <Button variant="ghost" className="mb-4 text-gray-900 hover:bg-gray-200">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Regulations
          </Button>
        </Link>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{regulation.name}</h1>
          <p className="text-xl text-gray-700">Complete Regulations Guide</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Badge className={getConsentBadge(regulation.consent_type)}>
              {regulation.consent_type}
            </Badge>
            {regulation.euthanasia_legal !== 'Illegal' && (
              <Badge className={regulation.euthanasia_legal === 'Legal' ? 'bg-green-700 text-white' : 'bg-orange-600 text-white'}>
                Euthanasia {regulation.euthanasia_legal}
              </Badge>
            )}
          </div>
        </div>

        {/* Tabs */}
        {regulation && (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 bg-gray-200">
              <TabsTrigger value="overview" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Overview
              </TabsTrigger>
              <TabsTrigger value="procedures" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Procedures
              </TabsTrigger>
              <TabsTrigger value="statistics" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Statistics
              </TabsTrigger>
              <TabsTrigger value="euthanasia" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Euthanasia {regulation.euthanasia_legal === 'Illegal' && '(Illegal)'}
              </TabsTrigger>
              <TabsTrigger value="visa" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Medical Visa
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="text-gray-900">Quick Facts</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-700 font-semibold mb-1">Legal Status</p>
                    <p className="text-gray-900">{regulation.organ_donation_legal}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-semibold mb-1">Living Donation</p>
                    <p className="text-gray-900">{regulation.living_donation_allowed ? 'Allowed' : 'Not Allowed'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-semibold mb-1">Minimum Age</p>
                    <p className="text-gray-900">{regulation.min_age} years</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-semibold mb-1">Blood Donation Interval</p>
                    <p className="text-gray-900">{regulation.blood_donation_interval} days</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-semibold mb-1">Consent System</p>
                    <p className="text-gray-900">{regulation.consent_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-semibold mb-1">Regulatory Body</p>
                    <p className="text-gray-900">{regulation.regulatory_body}</p>
                  </div>
                </CardContent>
              </Card>

              {regulation.website && (
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Official Website</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <a href={regulation.website} target="_blank" rel="noopener noreferrer" className="text-red-700 hover:underline flex items-center">
                      {regulation.website}
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </a>
                  </CardContent>
                </Card>
              )}

              {regulation.organs_allowed && (
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Organs Allowed for Donation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {regulation.organs_allowed.map((organ, index) => (
                        <Badge key={index} variant="outline" className="text-gray-900 border-gray-900">
                          {organ}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {regulation.blood_regulations && (
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Blood Donation Regulations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{regulation.blood_regulations}</p>
                  </CardContent>
                </Card>
              )}

              {/* Foreign Nationals Policy - Enhanced */}
              <Card className="shadow-md border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-700" />
                    Foreign Nationals & Medical Tourism Policy
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-2">
                    Rules and regulations for non-citizens seeking transplants or organ donation
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {regulation.foreign_national_policy && (
                    <div>
                      <p className="text-sm text-gray-700 font-semibold mb-2">General Policy</p>
                      <p className="text-gray-900 leading-relaxed">{regulation.foreign_national_policy}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-blue-200">
                    {regulation.foreign_national_transplant_allowed !== undefined && (
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-sm text-gray-700 font-semibold mb-2">Can Foreigners Receive Transplants?</p>
                        <div className="flex items-center gap-2">
                          {regulation.foreign_national_transplant_allowed ? (
                            <>
                              <CheckCircle className="h-5 w-5 text-green-700" />
                              <Badge className="bg-green-700 text-white">Yes, Allowed</Badge>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-5 w-5 text-red-700" />
                              <Badge className="bg-red-700 text-white">No, Citizens/Residents Only</Badge>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {regulation.foreign_national_donation_allowed !== undefined && (
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-sm text-gray-700 font-semibold mb-2">Can Foreigners Donate Organs?</p>
                        <div className="flex items-center gap-2">
                          {regulation.foreign_national_donation_allowed ? (
                            <>
                              <CheckCircle className="h-5 w-5 text-green-700" />
                              <Badge className="bg-green-700 text-white">Yes, Allowed</Badge>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-5 w-5 text-red-700" />
                              <Badge className="bg-red-700 text-white">No, Citizens/Residents Only</Badge>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {regulation.foreign_national_requirements && (
                    <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm text-gray-700 font-semibold mb-2">Requirements for Foreign Nationals</p>
                      <p className="text-gray-900 leading-relaxed text-sm">{regulation.foreign_national_requirements}</p>
                    </div>
                  )}

                  {!regulation.foreign_national_policy && regulation.foreign_national_transplant_allowed === undefined && (
                    <p className="text-gray-600 text-sm italic">
                      Contact the regulatory body or transplant center directly for information about foreign national eligibility.
                    </p>
                  )}
                </CardContent>
              </Card>

              {regulation.risks && (
                <Card className="shadow-md border-orange-200">
                  <CardHeader>
                    <CardTitle className="text-gray-900 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
                      Risks & Medical Considerations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{regulation.risks}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Procedures Tab */}
            <TabsContent value="procedures" className="space-y-6">
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="text-gray-900">Transplant Procedures by Organ</CardTitle>
                  <p className="text-sm text-gray-600 mt-2">Detailed procedure information based on international medical standards</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {regulation.organs_allowed?.map((organ, index) => {
                      const procedure = regulation.transplant_procedures?.[organ];
                      return (
                        <div key={index} className="border-b border-gray-200 pb-6 last:border-0">
                          <h4 className="font-bold text-gray-900 mb-3 text-lg capitalize">{organ} Transplant</h4>
                          {procedure ? (
                            <p className="text-gray-700 leading-relaxed">{procedure}</p>
                          ) : (
                            <p className="text-gray-700 text-sm italic">
                              Detailed procedure information for {organ} transplantation follows international medical protocols. 
                              Consult with your transplant center for country-specific procedures and success rates.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {regulation.waiting_times && (
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Average Waiting Times</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(regulation.waiting_times).map(([organ, time]) => (
                        <div key={organ} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                          <span className="font-semibold text-gray-900 capitalize">{organ}</span>
                          <span className="text-red-700 font-bold">{time}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Statistics Tab */}
            <TabsContent value="statistics" className="space-y-6">
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="text-gray-900">Transplant Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  {regulation.statistics ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.totalTransplantsPerYear?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">Total Transplants/Year</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.kidneyTransplants?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">Kidney Transplants</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.liverTransplants?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">Liver Transplants</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.heartTransplants?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">Heart Transplants</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.lungTransplants?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">Lung Transplants</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.waitingList?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">On Waiting List</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.deceasedDonors?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">Deceased Donors/Year</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {regulation.statistics.livingDonors?.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">Living Donors/Year</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700">Detailed statistics not available for this country.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Euthanasia Tab - Only show if not Illegal */}
            {regulation.euthanasia_legal !== 'Illegal' && (
              <TabsContent value="euthanasia" className="space-y-6">
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Euthanasia & Assisted Dying Laws</CardTitle>
                    <p className="text-sm text-gray-600 mt-2">Legal status and regulations for {regulation.name}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-700 font-semibold mb-1">Legal Status</p>
                        <Badge className={
                          regulation.euthanasia_legal === 'Legal' ? 'bg-green-700 text-white' :
                          regulation.euthanasia_legal === 'Partial' ? 'bg-orange-600 text-white' :
                          'bg-gray-700 text-white'
                        }>
                          {regulation.euthanasia_legal}
                        </Badge>
                      </div>
                      {regulation.organ_donation_after_euthanasia && (
                        <div>
                          <p className="text-sm text-gray-700 font-semibold mb-1">Organ Donation After Euthanasia</p>
                          <p className="text-gray-900">{regulation.organ_donation_after_euthanasia}</p>
                        </div>
                      )}
                    </div>

                    {regulation.euthanasia_details && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-gray-700 font-semibold mb-2">Legal Details</p>
                        <p className="text-gray-900 leading-relaxed">{regulation.euthanasia_details}</p>
                      </div>
                    )}

                    {regulation.euthanasia_requirements && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700 font-semibold mb-2">Legal Requirements</p>
                        <p className="text-gray-900 leading-relaxed">{regulation.euthanasia_requirements}</p>
                      </div>
                    )}

                    {/* Foreigner Access Information */}
                    {regulation.euthanasia_foreigners_allowed !== undefined && (
                      <div className={`mt-4 p-4 rounded-lg border-2 ${
                        regulation.euthanasia_foreigners_allowed 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-start gap-3">
                          {regulation.euthanasia_foreigners_allowed ? (
                            <CheckCircle className="h-5 w-5 text-green-700 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-700 mt-0.5 flex-shrink-0" />
                          )}
                          <div>
                            <p className="text-sm text-gray-700 font-semibold mb-2">
                              Access for Foreigners/Non-Residents
                            </p>
                            <p className={`font-bold text-lg mb-2 ${
                              regulation.euthanasia_foreigners_allowed 
                                ? 'text-green-700' 
                                : 'text-red-700'
                            }`}>
                              {regulation.euthanasia_foreigners_allowed 
                                ? '✅ Foreigners ARE Allowed' 
                                : '❌ Citizens/Residents Only'}
                            </p>
                            {regulation.euthanasia_foreigner_details && (
                              <p className="text-gray-900 leading-relaxed text-sm">
                                {regulation.euthanasia_foreigner_details}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {regulation.euthanasia_states && regulation.euthanasia_states.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-bold text-gray-900 mb-3">State/Region Specific Laws</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {regulation.euthanasia_states.map((state, index) => (
                            <Card key={index} className="border-green-200">
                              <CardContent className="pt-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="font-bold text-gray-900">{state.name}</h5>
                                  <Badge className="bg-green-700 text-white">{state.status}</Badge>
                                </div>
                                {state.year && (
                                  <p className="text-xs text-gray-600 mb-2">Legal since {state.year}</p>
                                )}
                                <p className="text-sm text-gray-700 mb-1"><strong>Types Allowed:</strong></p>
                                <p className="text-sm text-gray-700">{state.types}</p>
                                {state.requirements && (
                                  <>
                                    <p className="text-sm text-gray-700 mt-2 mb-1"><strong>Requirements:</strong></p>
                                    <p className="text-sm text-gray-700">{state.requirements}</p>
                                  </>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {regulation.euthanasia_sources && regulation.euthanasia_sources.length > 0 && (
                      <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-sm text-gray-700 font-semibold mb-3">Official Sources & References</p>
                        <ul className="space-y-2">
                          {regulation.euthanasia_sources.map((source, index) => (
                            <li key={index} className="text-sm text-gray-700 flex items-start">
                              <span className="mr-2">•</span>
                              <span>{source}</span>
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-gray-600 mt-3 italic">
                          Information verified from government sources and official legislation.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
            
            {/* Show message if euthanasia is illegal */}
            {regulation.euthanasia_legal === 'Illegal' && (
              <TabsContent value="euthanasia" className="space-y-6">
                <Card className="shadow-md border-red-200">
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Euthanasia is Illegal</h3>
                      <p className="text-gray-700 mb-4">
                        Euthanasia and assisted suicide are illegal in {regulation.name}. 
                        Assisting someone to die can result in criminal charges.
                      </p>
                      <p className="text-sm text-gray-600">
                        Passive euthanasia (withdrawal of life support) may be permitted in certain circumstances with proper medical and legal authorization.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Medical Visa Tab */}
            <TabsContent value="visa" className="space-y-6">
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="text-gray-900">Medical Tourism & Visa Information</CardTitle>
                  <p className="text-sm text-gray-600 mt-2">Complete guide for obtaining medical visa to {regulation.name}</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-700 font-semibold mb-1">Visa Required</p>
                      <p className="text-gray-900 font-medium">{regulation.visa_required ? 'Yes' : 'No'}</p>
                    </div>
                    {regulation.visa_type && (
                      <div>
                        <p className="text-sm text-gray-700 font-semibold mb-1">Visa Type</p>
                        <p className="text-gray-900 font-medium">{regulation.visa_type}</p>
                      </div>
                    )}
                    {regulation.visa_processing_time && (
                      <div>
                        <p className="text-sm text-gray-700 font-semibold mb-1">Processing Time</p>
                        <p className="text-gray-900 font-medium">{regulation.visa_processing_time}</p>
                      </div>
                    )}
                    {regulation.visa_fees && (
                      <div>
                        <p className="text-sm text-gray-700 font-semibold mb-1">Visa Fees</p>
                        <p className="text-gray-900 font-medium">{regulation.visa_fees}</p>
                      </div>
                    )}
                    {regulation.visa_extension !== undefined && (
                      <div>
                        <p className="text-sm text-gray-700 font-semibold mb-1">Extension Allowed</p>
                        <p className="text-gray-900 font-medium">{regulation.visa_extension ? 'Yes' : 'No'}</p>
                      </div>
                    )}
                    {regulation.visa_embassy_contact && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-700 font-semibold mb-1">Embassy/Consulate Contact</p>
                        <p className="text-gray-900 font-medium">{regulation.visa_embassy_contact}</p>
                      </div>
                    )}
                    {regulation.visa_application_link && (
                      <div className="md:col-span-2 p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-gray-700 font-semibold mb-2">🌐 Official Visa Application Portal</p>
                        <a 
                          href={regulation.visa_application_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-green-700 hover:text-green-800 font-bold text-lg underline"
                        >
                          <ExternalLink className="h-5 w-5" />
                          Apply for Medical Visa → (Official Government Website)
                        </a>
                        <p className="text-xs text-gray-600 mt-2">
                          ⚠️ Always use official government websites to avoid scams and fraud.
                        </p>
                      </div>
                    )}
                    {!regulation.visa_application_link && regulation.visa_required && (
                      <div className="md:col-span-2 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-sm text-gray-700 font-semibold mb-2">Visa Application Information</p>
                        <p className="text-gray-700 text-sm">
                          Contact the nearest {regulation.name} embassy or consulate for visa application procedures. 
                          {regulation.visa_embassy_contact && ` ${regulation.visa_embassy_contact}`}
                        </p>
                      </div>
                    )}
                  </div>

                  {regulation.visa_requirements && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700 font-semibold mb-2">Required Documents</p>
                      <p className="text-gray-900 leading-relaxed">{regulation.visa_requirements}</p>
                    </div>
                  )}

                  {regulation.visa_steps && regulation.visa_steps.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-bold text-gray-900 mb-4">Step-by-Step Visa Application Process</h4>
                      <ol className="space-y-3">
                        {regulation.visa_steps.map((step, index) => (
                          <li key={index} className="flex items-start">
                            <span className="flex-shrink-0 w-8 h-8 bg-red-700 text-white rounded-full flex items-center justify-center font-bold mr-3 mt-0.5">
                              {index + 1}
                            </span>
                            <span className="text-gray-900 leading-relaxed">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  <Card className="border-blue-200 mt-4">
                    <CardContent className="pt-4">
                      <h4 className="font-bold text-gray-900 mb-2">Transplant Approval Process</h4>
                      <p className="text-gray-700 leading-relaxed">
                        {regulation.visa_requirements ? 
                          'After obtaining your medical visa, you will need to: (1) Complete evaluation by an approved transplant center, (2) Undergo comprehensive medical and psychological screening, (3) Provide proof of insurance or financial means to cover treatment costs, (4) Obtain approval from the transplant center\'s medical board, (5) Coordinate with organ procurement organization if applicable.' :
                          'Contact local healthcare providers and transplant centers directly for medical tourism procedures. Requirements may vary by institution.'}
                      </p>
                    </CardContent>
                  </Card>

                  {regulation.visa_application_link && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-3">
                        <ExternalLink className="h-5 w-5 text-blue-700 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-700 font-semibold mb-2">Apply Online</p>
                          <a 
                            href={regulation.visa_application_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:text-blue-800 font-medium underline text-sm"
                          >
                            Visit Official Government Visa Application Portal →
                          </a>
                          <p className="text-xs text-gray-600 mt-2">
                            This is the official government website for visa applications. Always use official sources to avoid scams.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-700 font-semibold mb-2">Important Notes</p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Visa requirements and processing times are subject to change</li>
                      <li>• Always verify current requirements with the official embassy or consulate</li>
                      <li>• Medical visas may have specific validity periods and entry restrictions</li>
                      <li>• Some countries require health insurance coverage for the duration of stay</li>
                      <li>• Keep copies of all medical documents and visa approvals</li>
                      {regulation.visa_application_link && (
                        <li>• Use only official government websites for visa applications to avoid fraud</li>
                      )}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
    </>
  );
};

export default RegulationDetail;
